import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GameStatePrivate, PlayerSeat, RoundResult, Tile } from '@shared/types';
import PlayerSlot from './PlayerSlot';
import DeckPile from './DeckPile';
import DiscardPile from './DiscardPile';
import OkeyIndicator from './OkeyIndicator';
import MeldGroup from '../melds/MeldGroup';
import TileRack from '../tiles/TileRack';
import RoundResultModal from '../ui/RoundResultModal';
import soundManager from '@/game/soundManager';

interface GameTableProps {
  state: GameStatePrivate;
  onDrawTile: () => void;
  onDrawDiscard: () => void;
  onDiscard: (tileId: string) => void;
  onOpenMelds: (melds: { tileIds: string[] }[], discardTileId: string) => void;
  onAddToMeld: (meldId: string, tileId: string, position: 'start' | 'end') => void;
  onSortTiles: (type: 'color' | 'number') => void;
  roundResult: RoundResult | null;
  onDismissResult: () => void;
  error: string | null;
}

// Determine which server seat maps to which visual position
// relative = 0:bottom(me), 1:left, 2:top, 3:right
function getRelativeSeat(serverSeat: PlayerSeat, mySeat: PlayerSeat): 0 | 1 | 2 | 3 {
  return ((serverSeat - mySeat + 4) % 4) as 0 | 1 | 2 | 3;
}

const AVATAR_MAP: Record<string, string> = {
  fox: '🦊', tiger: '🐯', lion: '🦁', bear: '🐻',
  panda: '🐼', koala: '🐨', raccoon: '🦝', owl: '🦉',
};

export const GameTable: React.FC<GameTableProps> = ({
  state,
  onDrawTile,
  onDrawDiscard,
  onDiscard,
  onOpenMelds,
  onAddToMeld,
  onSortTiles,
  roundResult,
  onDismissResult,
  error,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [draggingTileId, setDraggingTileId] = useState<string | null>(null);
  const draggingElementRef = useRef<HTMLElement | null>(null);
  const cloneRef = useRef<HTMLElement | null>(null);

  // Find my seat
  const myPlayer = state.players.find(p => p.id === state.myId);
  const mySeat = myPlayer?.seat ?? 0;

  // Map players to visual positions
  const playerAt = useCallback((relPos: 1 | 2 | 3) => {
    return state.players.find(p => getRelativeSeat(p.seat, mySeat) === relPos) ?? null;
  }, [state.players, mySeat]);

  const topPlayer = playerAt(2);
  const leftPlayer = playerAt(1);
  const rightPlayer = playerAt(3);

  const isMyTurn = state.currentTurnSeat === mySeat;
  const canDraw = isMyTurn && state.turnPhase === 'draw';
  const canDiscard = isMyTurn && state.turnPhase === 'discard';
  const hasOpened = myPlayer ? false : false; // tracked server-side

  // ---- Tile selection ----
  const handleTileClick = useCallback((tileId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(tileId)) next.delete(tileId);
      else next.add(tileId);
      return next;
    });
  }, []);

  // ---- Drag & Drop (Pointer Events) ----
  const handleTilePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>, tileId: string) => {
    if (!canDiscard && !state.players.find(p => p.id === state.myId)) return;

    e.currentTarget.setPointerCapture(e.pointerId);
    setDraggingTileId(tileId);
    draggingElementRef.current = e.currentTarget as HTMLElement;

    // Create floating clone
    const rect = e.currentTarget.getBoundingClientRect();
    const clone = e.currentTarget.cloneNode(true) as HTMLElement;
    clone.className = `${clone.className} tile-drag-clone`;
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.left = `${e.clientX}px`;
    clone.style.top = `${e.clientY}px`;
    document.body.appendChild(clone);
    cloneRef.current = clone;

    soundManager.play('pick');
  }, [canDiscard, state.myId, state.players]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggingTileId || !cloneRef.current) return;
    cloneRef.current.style.left = `${e.clientX}px`;
    cloneRef.current.style.top = `${e.clientY}px`;
  }, [draggingTileId]);

  const removeDragClone = useCallback(() => {
    if (cloneRef.current) {
      document.body.removeChild(cloneRef.current);
      cloneRef.current = null;
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!draggingTileId) return;

    // Find drop target
    removeDragClone();
    const elements = document.elementsFromPoint(e.clientX, e.clientY);

    for (const el of elements) {
      const zone = (el as HTMLElement).dataset.dropZone;
      const data = (el as HTMLElement).dataset.dropData ?? '';

      if (zone === 'discard' && canDiscard) {
        onDiscard(draggingTileId);
        soundManager.play('discard');
        setSelectedIds(new Set());
        break;
      }
      if (zone === 'meld') {
        const [meldId, pos] = data.split(':');
        if (meldId && pos) {
          onAddToMeld(meldId, draggingTileId, pos as 'start' | 'end');
          soundManager.play('place');
          setSelectedIds(new Set());
          break;
        }
      }
    }

    setDraggingTileId(null);
    draggingElementRef.current = null;
  }, [draggingTileId, canDiscard, onDiscard, onAddToMeld, removeDragClone]);

  // Cleanup clone on unmount
  useEffect(() => () => removeDragClone(), [removeDragClone]);

  // ---- Action handlers ----
  const handleDiscard = () => {
    if (selectedIds.size !== 1) return;
    const [tileId] = selectedIds;
    onDiscard(tileId);
    soundManager.play('discard');
    setSelectedIds(new Set());
  };

  const handleOpenMelds = () => {
    if (selectedIds.size === 0) return;
    // Group selected tiles as one meld for now
    // (Player can also build multi-meld via UI — advanced UI not shown here)
    const selectedList = Array.from(selectedIds);
    const lastTileId = selectedList[selectedList.length - 1];
    const meldTileIds = selectedList.slice(0, -1);

    if (meldTileIds.length < 3) {
      // Not enough for even one meld
      return;
    }

    onOpenMelds([{ tileIds: meldTileIds }], lastTileId);
    soundManager.play('meld');
    setSelectedIds(new Set());
  };

  const discardTopTile = state.discardPile.length > 0
    ? state.discardPile[state.discardPile.length - 1].tile
    : null;

  const myPlayerInfo = state.players.find(p => p.id === state.myId);
  const myAvatar = AVATAR_MAP[myPlayerInfo?.avatar ?? 'fox'] ?? '🦊';

  // Turn label
  const turnLabel = isMyTurn
    ? '🟢 SIRA SİZDE'
    : `⏳ ${state.players.find(p => p.seat === state.currentTurnSeat)?.nickname ?? '...'}'nin Sırası`;

  return (
    <div
      className="game-layout"
      onPointerMove={draggingTileId ? handlePointerMove : undefined}
      onPointerUp={draggingTileId ? handlePointerUp : undefined}
    >
      {/* HUD */}
      <div className="game-hud">
        <span className="hud-brand">OKEY 101</span>
        <div className="hud-info">
          <span>El {state.roundNumber}</span>
          <span style={{ color: isMyTurn ? 'var(--color-success)' : 'var(--text-muted)' }}>
            {turnLabel}
          </span>
        </div>
        <div className="hud-actions">
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {state.mode === '101' ? '101 Okey' : 'Normal Okey'}
          </span>
        </div>
      </div>

      {/* Table Area */}
      <div className="table-area">
        {/* Top Player */}
        {topPlayer && (
          <PlayerSlot
            player={{ ...topPlayer, isCurrentTurn: topPlayer.seat === state.currentTurnSeat }}
            position="top"
          />
        )}

        {/* Left Player */}
        {leftPlayer && (
          <PlayerSlot
            player={{ ...leftPlayer, isCurrentTurn: leftPlayer.seat === state.currentTurnSeat }}
            position="left"
          />
        )}

        {/* Felt Table */}
        <div className="table-felt">
          {/* Turn indicator */}
          <div className="turn-indicator">{turnLabel}</div>

          <div className="table-center">
            {/* Deck + Indicator + Discard */}
            <div className="deck-indicator-row">
              <DeckPile
                count={state.deckCount}
                canDraw={canDraw}
                onDraw={onDrawTile}
              />
              <OkeyIndicator okeyInfo={state.okeyInfo} />
              <DiscardPile
                topTile={discardTopTile}
                canDrawDiscard={canDraw && !!discardTopTile}
                onDrawDiscard={onDrawDiscard}
              />
            </div>

            {/* Melds on table */}
            <div className="table-melds">
              {state.melds.map(meld => (
                <MeldGroup
                  key={meld.id}
                  meld={meld}
                  draggingTileId={canDiscard ? draggingTileId : null}
                  onDropToMeld={onAddToMeld}
                />
              ))}
            </div>
          </div>

          {/* Okey joker stars (bottom right of table) */}
          {state.okeyInfo && (
            <div className="okey-stars">
              <div className="tile tile-back" style={{ width: 28, height: 38 }} />
              <div className="tile tile-back" style={{ width: 28, height: 38 }} />
            </div>
          )}
        </div>

        {/* Right Player */}
        {rightPlayer && (
          <PlayerSlot
            player={{ ...rightPlayer, isCurrentTurn: rightPlayer.seat === state.currentTurnSeat }}
            position="right"
          />
        )}
      </div>

      {/* Bottom Bar */}
      <div className="bottom-bar">
        {/* Local player info */}
        <div className="local-player-info">
          <div className="local-player-profile">
            <div className="local-player-avatar">{myAvatar}</div>
            <div>
              <div className="local-player-name">{myPlayerInfo?.nickname ?? 'Sen'}</div>
              <div className="local-player-score">{myPlayerInfo?.totalScore ?? 0}</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {state.myTiles.length} taş
          </div>
        </div>

        {/* Rack */}
        <div className="rack-container">
          <TileRack
            tiles={state.myTiles}
            selectedIds={selectedIds}
            onTileClick={handleTileClick}
            onTilePointerDown={handleTilePointerDown}
            draggingTileId={draggingTileId}
          />
        </div>

        {/* Action buttons */}
        <div className="action-row">
          <div className="action-left">
            <button className="icon-btn">☰</button>
            <button className="icon-btn">💬</button>
          </div>

          <div className="action-center">
            <button
              className="btn btn-green"
              onClick={onDrawTile}
              disabled={!canDraw}
            >
              ÇEK
            </button>
            <button
              className="btn btn-orange"
              onClick={handleOpenMelds}
              disabled={!canDiscard || selectedIds.size < 3}
            >
              AÇ / İŞLE
            </button>
            <button
              className="btn btn-red"
              onClick={handleDiscard}
              disabled={!canDiscard || selectedIds.size !== 1}
            >
              TAŞ AT
            </button>
          </div>

          <div className="action-right">
            <button className="btn btn-dark btn-sm" onClick={() => onSortTiles('color')}>
              RENK
            </button>
            <button className="btn btn-dark btn-sm" onClick={() => onSortTiles('number')}>
              SAYI
            </button>
          </div>
        </div>
      </div>

      {/* Error toast */}
      {error && (
        <div className="toast error">{error}</div>
      )}

      {/* Round result modal */}
      {roundResult && (
        <RoundResultModal
          result={roundResult}
          roundNumber={state.roundNumber}
          onDismiss={onDismissResult}
        />
      )}
    </div>
  );
};

export default GameTable;
